import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, Avatar } from "@/components/Brand";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { roleLabel } from "@/lib/crm";

export default function Profile() {
  const { user, signIn } = useAuth();
  const [f, setF] = useState({ name: user.name || "", title: user.title || "", phone: user.phone || "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const refreshUser = (data) => {
    const token = localStorage.getItem("avy_token");
    signIn(token, data);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Name cannot be empty");
    setBusy(true);
    try {
      const { data } = await api.patch("/profile", f);
      refreshUser(data);
      toast.success("Profile saved");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      refreshUser(data);
      toast.success("Photo updated");
    } catch (err) { toast.error(errMsg(err)); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div>
      <PageHeader title="Profile & Settings" subtitle="Manage how you appear across your workspace." />
      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <div className="rounded-2xl border bg-white p-6 text-center" data-testid="profile-card">
          <div className="relative inline-block">
            <Avatar user={user} size={120} testId="profile-avatar" />
            <button type="button" onClick={() => fileRef.current?.click()} className="absolute bottom-1 right-1 grid h-9 w-9 place-items-center rounded-full bg-[#3B185F] text-white shadow hover:bg-[#250E3F]" data-testid="profile-photo-btn">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="profile-photo-input" />
          </div>
          <div className="mt-4 font-display text-xl font-bold text-[#19082B]">{user.name}</div>
          {f.title && <div className="text-sm text-stone-500">{f.title}</div>}
          <div className="mt-2 inline-flex items-center rounded-full bg-[#F6F2FB] px-3 py-1 text-xs font-semibold text-[#3B185F]" data-testid="profile-role-badge">{roleLabel(user.role)}</div>
          <p className="mt-3 text-xs text-stone-400">Click the camera to upload your own photo.</p>
        </div>

        <form onSubmit={save} className="rounded-2xl border bg-white p-6 space-y-4" data-testid="profile-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Full name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="profile-name-input" /></div>
            <div className="space-y-1.5"><Label>Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Virtual Assistant" data-testid="profile-title-input" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input value={user.email} disabled data-testid="profile-email-input" /></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="(555) 000-0000" data-testid="profile-phone-input" /></div>
          </div>
          <div><Button type="submit" disabled={busy} className="bg-[#3B185F] hover:bg-[#250E3F]" data-testid="profile-save-btn">{busy ? "Saving…" : "Save changes"}</Button></div>
        </form>
      </div>
    </div>
  );
}
